// Guided tutorial (reference tool: Strumenti › Tutorial): a floating card with the step text, the highlighted panel or
// card (spotlight) and a demo in which an animated cursor moves on the fields and fills in an example. Mounted only while
// running; the page pauses the auto-save and restores the user's data in onClose.
import { useEffect, useRef, useState, type FC } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../../components/ds2/Button";
import useI18n from "../../../hooks/useTranslations";
import type { ConnectionType } from "../../../http/screwConnector/formulas";
import type { InputSectionId } from "./types";
import {
  TUTORIAL_EXAMPLE,
  TUTORIAL_STEPS,
  type TutorialControl,
  type TutorialPointer,
  type TutorialStep,
} from "./tutorialSteps";
import styles from "./Tutorial.module.css";

// form and report access, provided by the page (always reading the latest form values)
export type TutorialHost = {
  // a user edit of a numeric control (null = cleared)
  type: (control: TutorialControl, value: number | null) => void;
  // empty and editable: the example data only fills these
  isEmpty: (control: TutorialControl) => boolean;
  connectionType: () => ConnectionType;
  setConnectionType: (type: ConnectionType) => void;
  thickness1: () => number | null;
  openReportPreview: () => Promise<void>;
  closeReportPreview: () => void;
  isReportPreviewOpen: () => boolean;
};

// page layout controls, provided by the page
export type TutorialLayout = {
  openSection: (id: InputSectionId) => void;
  setNavOpen: (open: boolean) => void;
  expandCard: (id: "drawCard" | "solutionsCard") => void;
};

type TutorialProps = {
  host: TutorialHost;
  layout: TutorialLayout;
  onClose: () => void;
};

type Box = { left: number; top: number; right: number; bottom: number };

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));
const overlap = (a: Box, b: Box) =>
  Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) *
  Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
const query = <T extends Element = HTMLElement>(selector: string) => document.querySelector<T>(selector);

// scrolling speeds of the report preview and of the position card [px/s]
const SCROLL_REPORT = 190;
const SCROLL_POS = 85;
// breakpoint below which the sidebar is a drawer
const DRAWER_MAX_WIDTH = 1180;

// visible part (inside the window) of the elements matching the selector
const visibleBoxes = (selector: string): Box[] =>
  [...document.querySelectorAll(selector)]
    .map((e) => e.getBoundingClientRect())
    .map((r) => ({
      left: Math.max(0, r.left),
      top: Math.max(0, r.top),
      right: Math.min(window.innerWidth, r.right),
      bottom: Math.min(window.innerHeight, r.bottom),
    }))
    .filter((r) => r.right - r.left > 4 && r.bottom - r.top > 4);

const scrollParent = (el: HTMLElement): HTMLElement => {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const overflow = getComputedStyle(p).overflowY;
    if ((overflow === "auto" || overflow === "scroll") && p.scrollHeight > p.clientHeight + 5) return p;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
};

// the position card's DS2 Accordion: its summary (the toggle) and whether it is open
const posToggle = () => query("#posCard summary");
const posOpen = () => !!query<HTMLDetailsElement>("#posCard details")?.open;

const Tutorial: FC<TutorialProps> = ({ host, layout, onClose }) => {
  const t = useI18n().translation.screwConnectorModule.calculation.tutorial;
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);
  const step: TutorialStep = TUTORIAL_STEPS[index] ?? TUTORIAL_STEPS[0]!;
  const stepTexts: { title: string; text: string; note?: string } = t.steps[step.id];
  const last = index === TUTORIAL_STEPS.length - 1;

  const hostRef = useRef(host);
  hostRef.current = host;
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const cardRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const rippleRef = useRef<HTMLElement>(null);
  const spotRef = useRef<HTMLDivElement>(null);
  // the running demo: a new demo (or the closing) makes the older one stop at its next await
  const runRef = useRef(0);
  const placedRef = useRef(false);
  const highlightRef = useRef<HTMLElement | null>(null);
  // what the demos changed and the next steps put back
  const posOpenedRef = useRef(false);
  const reportOpenedRef = useRef(false);
  const threeWasRef = useRef<{ type: ConnectionType; t1: number | null; t1Set: boolean } | null>(null);

  // ---- floating card, spotlight, cursor ----
  // centred card; while a field is being filled it moves (to the window's edge) if it would cover it or, second, the
  // highlighted panel
  const place = (field: Box | null, section: Box | null, instant = false) => {
    const card = cardRef.current;
    if (!card) return;
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 16;
    const cx = Math.round((vw - cw) / 2);
    const cy = Math.round((vh - ch) / 2);
    const right = vw - cw - m;
    const bottom = vh - ch - m;
    const candidates: [number, number][] = [
      [cx, cy],
      [cx, bottom],
      [cx, m],
      [right, bottom],
      [right, m],
      [right, cy],
    ];
    const pad = field
      ? { left: field.left - 24, right: field.right + 24, top: field.top - 24, bottom: field.bottom + 24 }
      : null;
    let best = candidates[0]!;
    let bestCost = Infinity;
    candidates.forEach(([x, y], i) => {
      const box = { left: x, top: y, right: x + cw, bottom: y + ch };
      const cost = (pad ? overlap(box, pad) * 1000 : 0) + (section ? overlap(box, section) : 0) + i;
      if (cost < bestCost) {
        bestCost = cost;
        best = [x, y];
      }
    });
    if (instant) card.style.transition = "none";
    card.style.left = `${best[0]}px`;
    card.style.top = `${best[1]}px`;
    if (instant) {
      void card.offsetWidth;
      card.style.transition = "";
    }
  };

  // while scrolling, the card moves (animated) to the free space next to the visible contents; it moves only when the
  // new place covers clearly less than the current one (no flickering)
  const placeAvoiding = (boxes: Box[], force = false) => {
    const card = cardRef.current;
    if (!card) return;
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 16;
    const cx = Math.round((vw - cw) / 2);
    const cy = Math.round((vh - ch) / 2);
    const right = vw - cw - m;
    const bottom = vh - ch - m;
    const candidates: [number, number][] = [
      [right, bottom],
      [right, m],
      [right, cy],
      [cx, bottom],
      [cx, m],
      [cx, cy],
    ];
    const cost = (box: Box) => boxes.reduce((sum, b) => sum + overlap(box, b), 0);
    const currentCost = cost(card.getBoundingClientRect());
    let best: [number, number] | null = null;
    let bestCost = Infinity;
    for (let i = 0; i < candidates.length; i++) {
      const [x, y] = candidates[i]!;
      const c = cost({ left: x, top: y, right: x + cw, bottom: y + ch }) + i;
      if (c < bestCost) {
        bestCost = c;
        best = [x, y];
      }
    }
    if (best && (force || bestCost + 3000 < currentCost)) {
      const [x, y] = best;
      card.style.left = `${x}px`;
      card.style.top = `${y}px`;
    }
  };

  const spot = (r: DOMRect | null) => {
    const s = spotRef.current;
    if (!s) return;
    if (!r) {
      s.hidden = true;
      return;
    }
    const p = 6;
    s.hidden = false;
    s.style.left = `${Math.round(r.left - p)}px`;
    s.style.top = `${Math.round(r.top - p)}px`;
    s.style.width = `${Math.round(r.width + 2 * p)}px`;
    s.style.height = `${Math.round(r.height + 2 * p)}px`;
  };

  const hideCursor = () => {
    if (cursorRef.current) cursorRef.current.hidden = true;
  };

  const moveCursor = async (el: HTMLElement) => {
    const c = cursorRef.current;
    if (!c) return;
    el.scrollIntoView({ block: "center", behavior: "auto" });
    await sleep(280);
    const r = el.getBoundingClientRect();
    c.hidden = false;
    spot(r);
    // the card moves away from the field about to be filled
    place(r, highlightRef.current?.getBoundingClientRect() ?? null);
    const x = r.left + Math.min(Math.max(14, r.width * 0.4), r.width - 8);
    c.style.transform = `translate(${Math.round(x)}px,${Math.round(r.top + r.height / 2)}px)`;
    await sleep(1050);
  };

  const ripple = async () => {
    const r = rippleRef.current;
    if (!r) return;
    r.classList.remove(styles.go!);
    void r.offsetWidth;
    r.classList.add(styles.go!);
    await sleep(450);
  };

  const scrollTo = async (sc: HTMLElement, to: number, pxPerSecond: number, run: number, tick?: () => void) => {
    const from = sc.scrollTop;
    const dist = to - from;
    if (Math.abs(dist) < 2) return;
    const duration = Math.max(800, (Math.abs(dist) / pxPerSecond) * 1000);
    const t0 = performance.now();
    let lastTick = t0;
    while (run === runRef.current) {
      const now = performance.now();
      const k = Math.min(1, (now - t0) / duration);
      sc.scrollTop = from + dist * k;
      if (tick && now - lastTick > 650) {
        lastTick = now;
        tick();
      }
      if (k >= 1) break;
      await sleep(16);
    }
  };

  // ---- demo actions ----
  const pointerElement = (at: TutorialPointer): HTMLElement | null => {
    if ("control" in at) {
      // a disabled react-select renders no hidden named input: then its box, by the HTML control id
      const named = query(`[name="${at.control}"]`) ?? query(`#${at.control}`);
      if (!named) return null;
      // react-select keeps the name on a hidden input inside its box, and a zero-size input inside the visible
      // control: use the box's first child with a real size
      const field = named instanceof HTMLInputElement && named.type === "hidden" ? (named.parentElement ?? named) : named;
      const candidates = Array.from(field.querySelectorAll<HTMLElement>("input:not([type=hidden]), button, select"));
      return (
        candidates.find((candidate) => {
          const r = candidate.getBoundingClientRect();
          return r.width > 8 && r.height > 8;
        }) ?? field
      );
    }
    if ("tile" in at) {
      const type = at.tile === "current" ? hostRef.current.connectionType() : at.tile;
      return query(`[data-val="${type}"]`);
    }
    if ("viewport" in at) return query("#drawCard canvas")?.parentElement ?? query("#drawCard");
    const el = query(at.css);
    // a switch's input: point at its label
    return el?.closest<HTMLElement>("label") ?? el;
  };

  const typeInto = async (control: TutorialControl, value: string, run: number) => {
    const h = hostRef.current;
    h.type(control, null);
    for (let k = 1; k <= value.length; k++) {
      if (run !== runRef.current) return;
      hostRef.current.type(control, Number(value.slice(0, k)));
      await sleep(120);
    }
  };

  const ensureData = async () => {
    for (const control of Object.keys(TUTORIAL_EXAMPLE) as TutorialControl[]) {
      if (!hostRef.current.isEmpty(control)) continue;
      hostRef.current.type(control, TUTORIAL_EXAMPLE[control]);
      await sleep(120);
    }
    await sleep(500);
  };

  const closeReport = () => {
    if (!reportOpenedRef.current) return;
    reportOpenedRef.current = false;
    if (hostRef.current.isReportPreviewOpen()) hostRef.current.closeReportPreview();
  };

  const restorePos = () => {
    if (!posOpenedRef.current) return;
    posOpenedRef.current = false;
    if (posOpen()) posToggle()?.click();
  };

  const restoreThree = () => {
    const was = threeWasRef.current;
    if (!was) return;
    threeWasRef.current = null;
    hostRef.current.setConnectionType(was.type);
    if (was.t1Set) hostRef.current.type("spessore1", was.t1);
  };

  const scrollReport = async (run: number) => {
    const box = query("[data-tut='pv-root']");
    const sc = query("[data-tut='pv-scroll']");
    if (!box || !sc) return;
    box.scrollIntoView({ block: "start", behavior: "auto" });
    await sleep(350);
    spot(box.getBoundingClientRect());
    const sheets = () => visibleBoxes("[data-tut='pv-sheet']");
    sc.scrollTop = 0;
    await sleep(300);
    placeAvoiding(sheets(), true);
    await sleep(700);
    await scrollTo(sc, sc.scrollHeight - sc.clientHeight, SCROLL_REPORT, run, () => placeAvoiding(sheets()));
    await sleep(900);
  };

  const scrollPos = async (run: number) => {
    const card = query("#posCard");
    if (!card) return;
    card.scrollIntoView({ block: "start", behavior: "auto" });
    await sleep(500);
    spot(null);
    const items = () => visibleBoxes("#posCard svg, #posCard table");
    placeAvoiding(items(), true);
    const sc = scrollParent(card);
    const extra = card.getBoundingClientRect().bottom - (window.innerHeight - 24);
    if (extra > 20) await scrollTo(sc, sc.scrollTop + extra, SCROLL_POS, run, () => placeAvoiding(items()));
    await sleep(1200);
  };

  const runDemo = async (s: TutorialStep) => {
    const run = ++runRef.current;
    hideCursor();
    setDone(false);
    if (!s.demo) return;
    await sleep(350);
    for (const a of s.demo) {
      if (run !== runRef.current) return;
      if (a.kind === "ensure") {
        await ensureData();
        continue;
      }
      if (a.kind === "waitReport") {
        hideCursor();
        for (let i = 0; i < 70 && run === runRef.current; i++) {
          if (query("[data-tut='pv-sheet']")) break;
          await sleep(400);
        }
        await sleep(600);
        continue;
      }
      if (a.kind === "scrollReport") {
        hideCursor();
        await scrollReport(run);
        continue;
      }
      if (a.kind === "scrollPos") {
        hideCursor();
        await scrollPos(run);
        continue;
      }

      const el =
        a.kind === "point"
          ? pointerElement(a.at)
          : a.kind === "type"
            ? pointerElement({ control: a.control })
            : a.kind === "connection"
              ? pointerElement({ tile: a.type })
              : a.kind === "expandPos"
                ? posToggle()
                : // the preview button only while the report card is open (closed, it is in a closed <details>)
                  (query("[data-tut='reportPreviewBtn']:not(details:not([open]) *)") ?? query("#reportCard"));
      if (!el) continue;
      await moveCursor(el);
      if (run !== runRef.current) return;
      await ripple();
      if (run !== runRef.current) return;

      if (a.kind === "type") await typeInto(a.control, a.value, run);
      if (a.kind === "connection") {
        const h = hostRef.current;
        if (!threeWasRef.current) threeWasRef.current = { type: h.connectionType(), t1: h.thickness1(), t1Set: false };
        // thin plate / panel thickness that suits the type shown
        if (a.t1 != null) {
          threeWasRef.current.t1Set = true;
          h.type("spessore1", a.t1);
        }
        h.setConnectionType(a.type);
        await sleep(1200);
      }
      if (a.kind === "expandPos" && !posOpen()) {
        posOpenedRef.current = true;
        el.click();
        await sleep(500);
      }
      if (a.kind === "openReport" && !hostRef.current.isReportPreviewOpen()) {
        reportOpenedRef.current = true;
        void hostRef.current.openReportPreview();
      }
      await sleep(300);
    }
    if (run === runRef.current) setDone(true);
    await sleep(500);
    if (run === runRef.current) hideCursor();
  };

  // ---- steps ----
  const showStep = async (i: number) => {
    const s = TUTORIAL_STEPS[i];
    if (!s) return;
    if (!s.keepReport) closeReport();
    if (!s.keepPos) restorePos();
    if (!s.keepThree) restoreThree();
    highlightRef.current = null;
    spot(null);
    place(null, null, !placedRef.current);
    placedRef.current = true;

    const target = s.target;
    const inSidebar = !!target && ("section" in target || "css" in target);
    layoutRef.current.setNavOpen(window.innerWidth <= DRAWER_MAX_WIDTH && inSidebar);
    if (target && "section" in target) {
      layoutRef.current.openSection(target.section);
      // <details> is controlled, but opening the DOM node directly makes the content measurable now: the state
      // update keeps it open on the next render, and the cursor does not end up on a hidden section.
      const details = query<HTMLDetailsElement>(`#${target.section}`);
      if (details && !details.open) details.open = true;
    }
    if (target && "card" in target && (target.card === "drawCard" || target.card === "solutionsCard")) {
      layoutRef.current.expandCard(target.card);
    }
    const el = !target
      ? null
      : "section" in target
        ? query(`#${target.section}`)
        : "card" in target
          ? query(`#${target.card}`)
          : query(target.css);
    if (el) {
      highlightRef.current = el;
      if (el.closest("nav")) el.scrollIntoView({ block: "nearest", behavior: "auto" });
      else {
        el.style.scrollMarginTop = "16px";
        el.scrollIntoView({ block: "start", behavior: "auto" });
      }
      requestAnimationFrame(() => {
        if (highlightRef.current === el) spot(el.getBoundingClientRect());
      });
    }
    void runDemo(s);
  };

  useEffect(() => {
    void showStep(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const close = () => {
    runRef.current++;
    closeReport();
    restorePos();
    threeWasRef.current = null;
    hideCursor();
    spot(null);
    onCloseRef.current();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // unmounted: any running demo stops at its next await
      runRef.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <>
      <div ref={spotRef} className={styles.spot} hidden />
      <div ref={cursorRef} className={styles.cursor} hidden>
        <i ref={rippleRef} className={styles.ripple} />
      </div>
      <div ref={cardRef} className={styles.card} role="dialog" aria-label={t.dialogLabel} aria-live="polite">
        <div className={styles.step}>{t.stepOf(index + 1, TUTORIAL_STEPS.length)}</div>
        <h3>{stepTexts.title}</h3>
        <p>{stepTexts.text}</p>
        {stepTexts.note ? <div className={styles.note}>{stepTexts.note}</div> : null}
        <div className={styles.status}>
          {step.demo ? (
            <>
              {done ? <span className={styles.done}>✓ {t.done}</span> : null}
              <Button variant="plain" onClick={() => void runDemo(step)}>
                ↻ {t.redo}
              </Button>
            </>
          ) : null}
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={close}>
            {t.skip}
          </Button>
          <span className={styles.actionsRight}>
            {index > 0 ? (
              <Button variant="secondary" onClick={() => setIndex(index - 1)}>
                {t.back}
              </Button>
            ) : null}
            <Button onClick={() => (last ? close() : setIndex(index + 1))}>{last ? t.finish : t.next}</Button>
          </span>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default Tutorial;
